import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonDictSetComp = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Advanced Structures</div>
                <h1>Dict & Set Comprehensions</h1>
                <p className="hero-lead">
                    Extend the power of declarative logic to Hash Maps and Sets.
                    Plus, learn the "Missing Link" of Python iteration: The Generator Expression.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Once you master list comprehensions, you unlock the ability to construct <em>any</em> collection type in Python
                    using the same elegant syntax.
                </p>
                <p>
                    However, the most powerful feature in this family isn't a collection at the all. It's the <strong>Generator Expression</strong>.
                    It allows you to process lazy, infinite streams of data with zero memory overhead, using the exact same syntax you just learned.
                </p>
            </div>

            <div className="content-section">
                <h2>Dictionary Comprehensions</h2>
                <p>
                    This is the cleanest way to transform data into a lookup table.
                    The syntax is similar to a list comprehension, but you specify a <code>key: value</code> pair.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Syntax: {`{k: v for item in iterable}`}</span>
                    </div>
                    <CodeBlock language="python" code={`users = [
    ("Alice", 25, "Engineer"),
    ("Bob", 30, "Manager"),
    ("Charlie", 22, "Intern")
]

# Create a lookup table (Name -&gt; Job)
user_jobs = {name: job for name, age, job in users}
# Output: {'Alice': 'Engineer', 'Bob': 'Manager', 'Charlie': 'Intern'}

# Use it for O(1) lookups
print(user_jobs["Alice"]) # 'Engineer'`} />
                </div>

                <h3>Common Pattern: Reversing a Dictionary</h3>
                <p>
                    Interviews often ask you to swap keys and values.
                </p>
                <CodeBlock language="python" code={`original = {'A': 1, 'B': 2, 'C': 3}

# Warning: If multiple keys have the same value, data will be lost!
reversed_dict = {v: k for k, v in original.items()}
# Output: {1: 'A', 2: 'B', 3: 'C'}`} />
            </div>

            <div className="content-section">
                <h2>Set Comprehensions</h2>
                <p>
                    Set comprehensions look identical to Dictionary comprehensions, but without the colon.
                    They are perfect for gathering <strong>unique</strong> attributes from a list of objects.
                </p>
                <CodeBlock language="python" code={`tags = ["python", "javascript", "PYTHON", "Go", "javascript"]

# Normalize and deduplicate in one step
unique_tags = {t.lower() for t in tags}

# Output: {'python', 'javascript', 'go'} (Order is not guaranteed)`} />
            </div>

            <div className="content-section">
                <h2>The Crown Jewel: Generator Expressions</h2>
                <p>
                    This is arguably more important than list comprehensions.
                    If you replace the square brackets <code>[]</code> with parentheses <code>()</code>, you do <strong>NOT</strong> get a tuple.
                    (Tuples are immutable; you can't construct them dynamically like this).
                </p>
                <p>
                    Instead, you create a <strong>Generator Object</strong>.
                </p>

                <h3>Eager (List) vs Lazy (Generator)</h3>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>List Component `[]`</th>
                                <th>Generator Expression `()`</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>Result</th>
                                <td>A complete list in RAM.</td>
                                <td>An iterator object (Recipe).</td>
                            </tr>
                            <tr>
                                <th>Memory</th>
                                <td>Linearly proportional to size (~8MB for 1M ints).</td>
                                <td>Constant (~120 bytes).</td>
                            </tr>
                            <tr>
                                <th>Speed</th>
                                <td>Fast, but pauses program to build entire list.</td>
                                <td>Instant startup. Calculates items one by one.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Memory Profiling</span>
                    </div>
                    <CodeBlock language="python" code={`import sys

# 1. List Comprehension (Eager)
# Creates 1 million items in memory immediately
list_comp = [x*2 for x in range(1000000)]
print(sys.getsizeof(list_comp)) 
# Output: ~8,448,728 bytes (8 MB)

# 2. Generator Expression (Lazy)
# No items created yet. Just the "formula".
gen_exp = (x*2 for x in range(1000000))
print(sys.getsizeof(gen_exp))
# Output: ~128 bytes (Tiny!)

# How to use the generator? Loop over it!
# for num in gen_exp: ...`} />
                </div>
            </div>

            <div className="content-section">
                <h2>When to use Generators?</h2>
                <p>
                    You should default to using Generators unless you actually <em>need</em> a list.
                </p>
                <ul>
                    <li><strong>Use Lists if:</strong> You need to access indices (`data[4]`), strict length (`len(data)`), or iterate multiple times.</li>
                    <li><strong>Use Generators if:</strong> You are just looping over the data once (e.g., `sum()`, `max()`, `any()`, `all()`, or a `for` loop).</li>
                </ul>

                <h3>The "Syntactic Sugar" for Functions</h3>
                <p>
                    If you pass a generator expression as the <em>only argument</em> to a function, you can omit the parentheses.
                </p>
                <CodeBlock language="python" code={`numbers = [1, 5, 10, 20]

#  Double Parentheses (Redundant)
total = sum((x*x for x in numbers))

# Implicit Parentheses (Clean)
total = sum(x*x for x in numbers)

# Check if ANY number is negative
is_neg = any(n < 0 for n in numbers)`} />
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls</h2>
                <div className="pitfall-card">
                    <h3> The "Missing" Tuple Comprehension</h3>
                    <p><strong>The Trap:</strong> Beginners often try `tuple(x for x in range(3))` thinking it's special syntax.</p>
                    <p><strong>The Reality:</strong> It's just a Generator Expression passed to the `tuple()` constructor. It works, but it runs the generator to exhaustion and builds a tuple.</p>
                </div>

                <div className="pitfall-card">
                    <h3> Modifying the Source while Iterating</h3>
                    <p><strong>The Trap:</strong> Just like loops, do not modify the dictionary/set you are iterating over inside the comprehension.</p>
                    <CodeBlock language="python" code={`#  RuntimeError: dictionary changed size during iteration
data = {1: 'a', 2: 'b'}
new = {k: v for k, v in data.items() if data.pop(k)} # DANGER!`} />
                </div>
            </div>

        </div>
    );
};

export default PythonDictSetComp;



